import { cellAt, inBounds } from './board.js';
import { effectiveColor } from './specials.js';
import type { Board, Coord } from './types.js';
import { coordKey } from './types.js';

const DIRS: readonly Coord[] = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

/**
 * 4-connected component of cells reachable from `start` whose color
 * equals start.color and whose tile is not 'locked'. The start cell
 * itself is always included, even if locked (it's the player anchor).
 *
 * `phase` is the flipping-cell side selector (0 or 1). Defaults to 0
 * for solver / non-time-aware callers.
 */
export const getControlledRegion = (
  board: Board,
  start: Coord,
  phase: 0 | 1 = 0,
): ReadonlySet<string> => {
  const startCell = cellAt(board, start);
  const target = effectiveColor(startCell, phase);
  const visited = new Set<string>();
  const stack: Coord[] = [start];
  visited.add(coordKey(start));

  while (stack.length > 0) {
    const cur = stack.pop() as Coord;
    for (const d of DIRS) {
      const nx = cur.x + d.x;
      const ny = cur.y + d.y;
      const next: Coord = { x: nx, y: ny };
      if (!inBounds(board, next)) continue;
      const k = coordKey(next);
      if (visited.has(k)) continue;
      const c = cellAt(board, next);
      if (c.tile === 'locked') continue;
      if (effectiveColor(c, phase) !== target) continue;
      visited.add(k);
      stack.push(next);
    }
  }
  return visited;
};

export const isCornerConnected = (board: Board, phase: 0 | 1 = 0): boolean => {
  const region = getControlledRegion(board, board.start, phase);
  return region.has(coordKey(board.end));
};
